import * as webStorage from '@com.rakuten.esd/myr-web-storage';
import * as qs from 'qs';
import { TrackJS } from 'trackjs';
import {
    ErrorResolution,
    ErrorResolutionTypes,
} from '../../app/common/errorResolution/errorResolution';
import ErrorRedirectService from '../../routes/error/errorRedirect.service';
import store from '../../utils/store';
import { HttpStatus } from '../httpStatus.constant';
import { MyrAPIEndpoints } from './myrAPIEndpoints.constant';
import { IMyrAPIErrorResponseBody } from './myrAPIErrorResponseBody';
import { ServiceErrorCodes } from '../serviceErrorCodes.constant';
import { addRatEvent } from '../../utils/ratClickEvents';
import {
    CANNOT_BE_DEFAULT_MSG,
    CANNOT_BE_DEFAULT_TITLE,
} from '../../details/paymentMethods/cards/cardsActionType';

type MyrAPIMethod = 'GET' | 'DELETE' | 'PUT' | 'POST' | 'PATCH';

class MyrAPI {
    public sendRequest<T>(
        endpoint: string,
        method: MyrAPIMethod,
        contentBody?: any,
        credentials?: string,
        contentType?: string,
    ): Promise<T> {
        return new Promise(async (resolve, reject) => {
            const abortController = new AbortController();

            const requestInit = this.getRequestInit(
                method,
                contentBody,
                abortController.signal,
                credentials,
                contentType,
            );
            const endpointUrl = this.getEndpointUrl(endpoint, method, contentBody);

            if (!requestInit || !endpointUrl) {
                return reject({ type: ErrorResolutionTypes.GENERIC_MESSAGE } as ErrorResolution);
            }
            try {
                const fetchResponse = await fetch(endpointUrl, requestInit);

                const { ok, status } = fetchResponse;
                if (ok) {
                    // HTTP 207 Multi-Status: currently only the password endpoint returns this,
                    // signalling password updated but session revocation failed (AuthV2 inline revoke).
                    // Reject as PARTIAL_SUCCESS so callers can handle the partial outcome separately.
                    if (status === HttpStatus.MULTI_STATUS) {
                        return reject({
                            type: ErrorResolutionTypes.PARTIAL_SUCCESS,
                        } as ErrorResolution);
                    }

                    try {
                        const json = await fetchResponse.json();
                        resolve(json);
                    } catch (e) {
                        resolve(null!);
                    }
                } else {
                    if (
                        status === HttpStatus.UNPROCESSABLE_ENTITY ||
                        status === HttpStatus.CONFLICT
                    ) {
                        const errorResponseBody = await fetchResponse.json();
                        if (
                            (endpoint === MyrAPIEndpoints.EMAIL_VERIFY ||
                                endpoint === MyrAPIEndpoints.EMAIL_UPDATE) &&
                            (errorResponseBody?.error?.code ===
                                ServiceErrorCodes.EMAIL_VERIFY_CONFLICT ||
                                errorResponseBody?.error?.code ===
                                    ServiceErrorCodes.EMAIL_VERIFY_CONFLICT_JP)
                        ) {
                            reject(await this.resolveOtherErrorResponses(endpoint, fetchResponse));
                        }

                        if (
                            status === HttpStatus.UNPROCESSABLE_ENTITY &&
                            endpoint.includes(MyrAPIEndpoints.CARD)
                        ) {
                            return reject({
                                errorMessageId: CANNOT_BE_DEFAULT_MSG,
                                title: CANNOT_BE_DEFAULT_TITLE,
                                type: ErrorResolutionTypes.ERROR_CODE_MESSAGE,
                            });
                        }

                        const errorResolution =
                            this.resolveErrorResponseWithMessage(errorResponseBody);
                        return reject(errorResolution);
                    } else {
                        if (status === HttpStatus.BAD_REQUEST) {
                            const errorResponseBody = await fetchResponse.json();
                            if (errorResponseBody?.error?.code === 'myr-6029') {
                                addRatEvent('password_update', 'password_invalid');
                            }
                        }
                        const errorResolution = await this.resolveOtherErrorResponses(
                            endpoint,
                            fetchResponse,
                        );
                        return reject(errorResolution);
                    }
                }
            } catch (error) {
                if (error) {
                    TrackJS.track(error);
                    return reject({
                        type: ErrorResolutionTypes.GENERIC_MESSAGE,
                    } as ErrorResolution);
                }
            }
        });
    }

    private getEndpointUrl(endpoint: string, method?: MyrAPIMethod, contentBody?: any): string {
        const BASE_URL = store.getState().app.appConfig.BASE_API_URL;
        const endpointUrl = new URL(`${BASE_URL}${endpoint}`);
        const searchParams =
            method === 'GET' && contentBody ? new URLSearchParams(qs.stringify(contentBody)) : '';

        if (!endpointUrl || (method === 'GET' && contentBody && !searchParams)) {
            TrackJS.track('Invalid myr v2 endpoint url');
            ErrorRedirectService.unexpected();
            return '';
        }

        if (method === 'GET' && contentBody) {
            endpointUrl.search = searchParams.toString();
        }

        return endpointUrl.toString();
    }

    private getRequestHeaders(
        method: MyrAPIMethod,
        credentials?: string,
        contentType?: string,
    ): HeadersInit {
        const locale = store.getState().app.locale;
        const serviceUuid = store.getState().app.clientConfig.serviceUuid;

        const headers = new Headers();

        headers.append('Authorization', `Bearer ${webStorage.getAccessToken()}`);
        headers.append('serviceUuid', serviceUuid);

        if (credentials) {
            headers.append('X-Credential-Validation', `Password ${window.btoa(credentials)}`);
        }

        const applyServiceHeader = store.getState().app.clientConfig.applyServiceHeader;
        if (applyServiceHeader) {
            headers.append('X-Client-ID', store.getState().app.clientConfig.clientId);
        }

        if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
            headers.set('Content-Type', 'application/json; charset=UTF-8');
        }

        headers.set('Accept-Language', locale);
        if (contentType === 'json') {
            headers.set('Content-Type', 'application/json; charset=UTF-8');
        }

        return headers;
    }

    private getRequestInit(
        method: MyrAPIMethod,
        contentBody?: any,
        signal?: AbortSignal,
        credentials?: string,
        contentType?: string,
    ): RequestInit {
        const requestInit = {} as RequestInit;

        requestInit.method = method;

        requestInit.headers = this.getRequestHeaders(method, credentials, contentType);

        if (signal) {
            requestInit.signal = signal;
        }

        if (contentBody && method !== 'GET') {
            requestInit.body = JSON.stringify(contentBody);
        }

        return requestInit;
    }

    private resolveErrorResponseWithMessage({
        error: { code, errors },
    }: IMyrAPIErrorResponseBody): ErrorResolution {
        if (!code) {
            return { type: ErrorResolutionTypes.GENERIC_MESSAGE };
        }
        if (code === 'myr-6036') {
            addRatEvent('password_update', 'password_leaked');
            return {
                errorCode: code,
                type: ErrorResolutionTypes.ERROR_BOX,
            };
        } else if (code === 'myr-6035') {
            addRatEvent('password_update', 'password_weak');
        }

        const showErrorList = store.getState().app.clientConfig.showErrorList ?? true;
        if (!showErrorList || !errors || errors.length === 0) {
            return {
                errorCode: code,
                type: ErrorResolutionTypes.WARNING_CODE_MESSAGE,
            };
        }

        return {
            errorCode: code,
            errorMessages: errors.map((e) => e.message),
            type: ErrorResolutionTypes.ERROR_LIST,
        };
    }

    private async resolveOtherErrorResponses(
        endpoint: string,
        response: Response,
    ): Promise<ErrorResolution> {
        const { status } = response;
        TrackJS.track(`MyR API call to ${endpoint} returned status ${status}`);
        switch (status) {
            case HttpStatus.BAD_REQUEST:
                // ask users to clear cookies to perform subsequent actions
                return {
                    errorMessageId: 'notifications.try_different_page',
                    type: ErrorResolutionTypes.GENERIC_MESSAGE,
                };
            case HttpStatus.FORBIDDEN:
                try {
                    const {
                        error: { code },
                    } = await response.json();
                    switch (code) {
                        case ServiceErrorCodes.CHALLENGE_NOT_PASSED:
                            return {
                                errorCode: code,
                                type: ErrorResolutionTypes.WARNING_CODE_MESSAGE,
                            };
                        case ServiceErrorCodes.TOKEN_EXPIRED_ON_READ_WRITE_ACCESS:
                            ErrorRedirectService.reAuth();
                            return { type: ErrorResolutionTypes.RE_AUTH };
                        case ServiceErrorCodes.MEMBER_NOT_LINKED_ERROR:
                            ErrorRedirectService.noLinkage();
                            return { type: ErrorResolutionTypes.MEMBER_NOT_LINKED_ERROR };
                        default:
                    }
                } catch (e) {
                    // We were not able to read or process response json, backing off to log out redirection.
                }
                ErrorRedirectService.notLoggedIn();
                return { type: ErrorResolutionTypes.REDIRECTING };
            case HttpStatus.UNAUTHORIZED:
                ErrorRedirectService.notLoggedIn();
                return { type: ErrorResolutionTypes.REDIRECTING };
            case HttpStatus.NOT_FOUND:
                if (endpoint.startsWith(MyrAPIEndpoints.SUMMARY)) {
                    ErrorRedirectService.unexpected();
                    return { type: ErrorResolutionTypes.REDIRECTING };
                }

                return {
                    errorMessageId: 'notifications.try_different_page',
                    type: ErrorResolutionTypes.GENERIC_MESSAGE,
                };
            case HttpStatus.INTERNAL_SERVER_ERROR: {
                // If GPP is down and we receive 5xx error from the backend then don't redirect to error page
                if (endpoint.startsWith(MyrAPIEndpoints.POINTS)) {
                    return {
                        type: ErrorResolutionTypes.NONE,
                    };
                }
                const responseBody = await response.json();
                const {
                    error: { code },
                } = responseBody;
                if (ServiceErrorCodes.TOKEN_REVOCATION_FAILURE === code) {
                    ErrorRedirectService.unexpected();
                    return {
                        // ask users to clear cookies to perform subsequent actions
                        errorMessageId: 'notifications.try_different_page',
                        type: ErrorResolutionTypes.GENERIC_MESSAGE,
                    };
                }
                ErrorRedirectService.unavailable();
                return { type: ErrorResolutionTypes.REDIRECTING };
            }
            case HttpStatus.BAD_GATEWAY:
            case HttpStatus.SERVICE_UNAVAILABLE:
            case HttpStatus.GATEWAY_TIMEOUT:
                // Currently there is a bug in load balancer config that
                // might cause in 502 & 503 issues happen more often than they should
                ErrorRedirectService.unavailable();
                return { type: ErrorResolutionTypes.REDIRECTING };
            case HttpStatus.TOO_MANY_REQUESTS:
                return { type: ErrorResolutionTypes.TOO_MANY_REQUESTS };
            case HttpStatus.CONFLICT:
                return { type: ErrorResolutionTypes.EMAIL_CONFLICT };
            case HttpStatus.PRECONDITION_FAILED:
                const json = await response.json();
                ErrorRedirectService.authError(json.r10Rejection);
                return {
                    type: ErrorResolutionTypes.REDIRECTING,
                };
            default:
                ErrorRedirectService.unexpected();
                return { type: ErrorResolutionTypes.REDIRECTING };
        }
    }
}

export const MyrAPIService = new MyrAPI();
